import { useEffect, useRef, useState } from 'react'
import './loot-keys-form.styles.scss';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ModalAlert, ModalConfirm } from '@/components/modal/modal.component';
import ManageContentInformation from '../blocks/information.component';
import ManageContentIconCropped from '../blocks/icon.component';
import ManageContentCategories from '../blocks/categories.component';
import { useSelector } from 'react-redux';
import { getDownloadURL, getStorage, ref, uploadBytes } from 'firebase/storage';
import { UseCloudFunction } from '@/utils/firebase.utils';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';
import ManageContentRewards from '../blocks/rewards.component';
import { membershipRestrictions, testMemberRestrictions } from '@/utils/membershipRestrictions';
import UpgradeRequiredModal from '@/components/SHARED/upgrade-required-modal/upgrade-required-modal.component';
import { RandomString } from '@/utils/randomString';
import BackButton from '@/components/SHARED/back-button/back-button.component';
import ImageContainer from '@/components/SHARED/image-container/image-container.component';
import ManageContentVisibility from '../blocks/visibility.component';
import ShowHideSettingsButton from '../blocks/show-hide-button.component';
import ElementDelete from '@/components/SHARED/element-delete/element-delete.component';
import useAnalytics from '@/utils/useAnalytics';
import { getOptimizedBlob } from '@/utils/getOptimizedBlob';
import ManageLootKeyCount from '../blocks/loot-key-count.component';

const LootKeysForm = ({
  gameElements,
  lootKeysFromDb,
  gameData,
  gameStats,
  playerList,
}) => {

  const membership = useSelector(state => state.userData.membership);
  const userProfile = useSelector(state => state.userData.userProfile);
  const screenSize = useSelector(state => state.contentData.screenSize);
  const [formData, setFormData] = useState({});
  const [alertMessage, setAlertMessage] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState(null);
  const [existingDescription, setExistingDescription] = useState(null);
  const [clearEditor, setClearEditor] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(null);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const { lootKeyPath } = useParams();
  const { trackEvent, trackException } = useAnalytics();
  const dataRef = useRef({});
  const location = useLocation();
  const approveRef = useRef();
  const navigate = useNavigate();
  const storage = getStorage();

  useEffect(() => {
    if (!gameData || !membership || !location) return;
    if (location.pathname.includes('/elements/lootkeys/edit')) return;
    const res = testMemberRestrictions({ 'gameData': gameData, 'membership': membership, 'create': true });
    if (res.disabled && res.disabledReason.includes('elements')) {
      const planRest = membershipRestrictions[membership.membership];
      const message = `While using the ${planRest.title}, you are only able to have ${planRest.elementCount} active Game Elements per game.`;
      setShowUpgradeModal(message);
      return;
    }
  }, [gameData, membership, location])

  useEffect(() => {
    if (!lootKeyPath || !lootKeysFromDb) { 
      dataRef.current = {
        'opt_visible': true,
        'opt_available': true,
        'opt_preview': true,
        'icon': '/images/icons/lootkey.png'
      };
      setFormData(dataRef.current);
      return;
    }
    const existing = Object.values(lootKeysFromDb).filter(e => e.path === lootKeyPath)[0];
    dataRef.current = {...existing};
    setFormData(dataRef.current);
    if (dataRef.current?.desc) {
      setExistingDescription(dataRef.current.desc);
    }
  }, [lootKeyPath, lootKeysFromDb])

  useEffect(() => {
    if (!lootKeysFromDb) { return; }
    let arr = [];
    for (let c of Object.values(lootKeysFromDb).filter(c => c.type === 'lootKey')) {
      if (c.opt_cat && !arr.includes(c.opt_cat)) {
        arr.push(c.opt_cat)
      }
    }
    setCategories(arr.sort())
  }, [lootKeysFromDb])

  function saveData(key, value) {
    if (key === 'name') {
      value = value.slice(0, 50);
    }
    let tempData = { ...dataRef.current };
    if (!value) {
      delete tempData[key];
      dataRef.current = { ...tempData };
    } else {
      dataRef.current = { ...dataRef.current, ...{ [key]: value } };
    }
    setFormData(dataRef.current);
  }

  function cancelCreate() {
    setConfirmMessage('Are you sure you want to cancel? All of your data will be lost.');
    approveRef.current = () => {
      dataRef.current = {
        'opt_visible': true,
        'opt_available': true,
        'opt_preview': true,
        'icon': '/images/icons/lootkey.png'
      };
      setFormData(dataRef.current);
      navigate(-1)
    }
  }

  async function handleSubmit() {
    if (location.pathname.includes('/preview/manager/')) {
      setAlertMessage('Sorry! Data cannot be saved in Preview Mode.');
      return;
    }
    let tempData = { ...dataRef.current }
    const reqs = ['name', 'opt_keyCount']
    for (let r of reqs) {
      if (!tempData[r]) {
        setAlertMessage('Please include a name and a key count for this Loot Key before saving.');
        return;
      }
    }
    // check for some kind of reward
    if (
      !tempData.opt_earnedPoints &&
      !tempData.opt_earnedCurrency &&
      !tempData.opt_earnedBadges?.length &&
      !tempData.opt_earnedItems?.length &&
      !tempData.opt_earnedPrizePacks?.length &&
      !tempData.opt_earnedMomentum?.length
    ) {
      setAlertMessage('Please include at least one reward for players to receive when redeeming this Loot Key.');
      setLoading(false);
      return;
    }
    setConfirmMessage('Are you sure you are ready to save this Loot Key? All of these settings can be changed later.')
    approveRef.current = () => {
      finishApproval()
    }
  }

  async function finishApproval() {
    setLoading(true);
    let tempData = { ...dataRef.current }
    console.log(tempData);
    if (tempData.icon.upload) {
      // tempData.icon.croppie.result({
      // 	type:'blob',
      // 	size: {'width': 352, 'height': 352},
      //     quality: 0.7,
      // })
      // .then((blob) => {
      // 	uploadImage(tempData.icon, blob)
      // 	return;
      // })
      const optimizedBlob = await getOptimizedBlob(tempData.icon.croppie, 25);
      console.log('Cropped image size in KB:', optimizedBlob.size / 1024);
      uploadImage(tempData.icon, optimizedBlob);
    } else if (tempData.icon.url) {
      saveLootKey(tempData.icon.url)
      return;
    } else if (tempData.icon) {
      saveLootKey(tempData.icon)
      return;
    } else {
      setAlertMessage('Something went wrong with the icon. Please try using a different image.');
      setLoading(false);
      return;
    }
  }

  function uploadImage(icon, blob) {
    const now = new Date().getTime();
    const path = `${userProfile.appId}/${now}-${icon.name}`;
    const iconRef = ref(storage, path)
    const metadata = {
      contentType: icon.type,
      customMetadata: {
        appId: userProfile.appId,
        email: userProfile.email
      }
    };
    uploadBytes(iconRef, blob, metadata).then(() => {
      getDownloadURL(iconRef)
        .then(async (url) => {
          saveLootKey(url);
          try {
            await UseCloudFunction(
              'saveToMyContent',
              {
                'gameId': gameData.gameId,
                'appId': userProfile.appId,
                'source': 'Loot Key Icons',
                'category': 'Images',
                'cropped': true,
                'contentData': {
                  'name': icon.name,
                  'url': url,
                  'type': 'image',
                  'id': `${now}_${RandomString(10)}_image`
                },
              }
            )
          } catch (error) {
            console.error('Error saving to my content:', error);
            setAlertMessage('Something went wrong while saving the icon to My Content. Error: ' + error.message);
            trackException(`Save Loot Key icon to My Content Error: ${error}`);
          }
          trackEvent(
            'image_saved_to_my_content',
            'Game Manager',
            `Loot Key icon ${icon.name} was saved to My Content for user ${userProfile.appId}.`,
            null,
            membership
          )
          return;
        })
    })
  }

  async function saveLootKey(iconUrl) {
    let tempData = { ...dataRef.current }
    console.log(tempData);
    const now = new Date().getTime();
    const gameCode = gameData.gameId.substring(gameData.gameId.indexOf('-') - 5, gameData.gameId.indexOf('-') + 6)
    let action = 'Edited';
    if (!tempData.id) {
      tempData.appId = userProfile.appId;
      tempData.gameId = gameData.gameId;
      tempData.id = now + '-' + gameCode + '-lootKey';
      tempData.status = 'active';
      tempData.type = 'lootKey';
      tempData.path = RandomString(18, 'abcdefghijklmnopqrstuvwxyz-_0123456789');
      action = 'Created';
    }
    tempData.icon = iconUrl;
    console.log(tempData);
    const res = await UseCloudFunction(
      'saveGameElement',
      {
        'gameId': gameData.gameId,
        'appId': userProfile.appId,
        'elementData': tempData,
        'userName': `${userProfile.fName} ${userProfile.lName}`,
        'action': action
      })
    console.log(res);
    if (res.error) {
      setAlertMessage('Something went wrong. Error: ' + res.error);
      setLoading(false);
      trackException(`Loot Key ${action} Error: ${res.error}`);
      return;
    }
    setAlertMessage('Loot Key saved successfully.');
    dataRef.current = {};
    setFormData(dataRef.current);
    setLoading(false);
    const event = action === 'Created' ? 'loot_key_created' : 'loot_key_edited';
    trackEvent(
      event,
      'Game Manager',
      `Loot Key ${tempData.id} was ${action.toLowerCase()}.`,
      null,
      membership
    )
    navigate(-1);
  }

  return (
    <div className={`create-loot-key-form ${screenSize}`}>
      <div className='form-grid'>
        <div className='g-card loot-keys-card'>
          <BackButton cancel={() => navigate(-1)} />
          <div className="g-space-1"></div>
          <div className='card-title'>
            <ImageContainer src='/images/icons/lootkey.png' alt='loot keys icon' className='head-icon' />
            Create and Edit Loot Keys
          </div>
          <hr />
          <span className='required meta'>* = Required</span>
          <div className="g-space-0-5"></div>
          <ManageContentInformation
            type='lootKey'
            saveData={saveData}
            formData={formData}
            existingDescription={existingDescription}
            clearEditor={clearEditor}
            setClearEditor={setClearEditor}
            textEditorLabel='Loot Key Description (optional)'
          />
          <div className='g-space-2'></div>
          <ManageContentRewards
            type='lootKey'
            saveData={saveData}
            formData={formData}
            gameElements={gameElements}
            gameStats={gameStats}
            text='redeeming this Loot Key'
            gameData={gameData}
          />
          <div className='g-space-1'></div>
          <ManageLootKeyCount
            gameData={gameData}
            saveData={saveData}
            formData={formData}
            disabled={loading}
            playerList={playerList}
          />
          <div className='g-space-1'></div>
          <ShowHideSettingsButton
            setShowAdvancedOptions={setShowAdvancedOptions}
            showAdvancedOptions={showAdvancedOptions} />
          <div className={`advanced-options ${showAdvancedOptions ? 'show' : 'hide'}`}>
            <div className='g-space-1'></div>
            <ManageContentIconCropped
              type='lootKey'
              saveData={saveData}
              formData={formData}
            />
            <div className='g-space-2'></div>
            <ManageContentCategories
              type='lootKey'
              saveData={saveData}
              formData={formData}
              categories={categories}
            />
            <div className='g-space-2'></div>
            <ManageContentVisibility
              type='lootKey'
              saveData={saveData}
              formData={formData}
              visibilityText="Turn visibility ON for this Loot Key. If toggled off, players can not redeem any of the Loot Keys, nor will it appear in their history."
              availabilityText="This Loot Key is able to be redeemed by players if it is open. If toggled off, players cannot redeem this Loot Key, but it will be visible in their history."
            />
            <div className="g-space-1"></div>
            <ShowHideSettingsButton
              showAdvancedOptions={showAdvancedOptions}
              setShowAdvancedOptions={setShowAdvancedOptions}
              below={true} />
          </div>
          <div className="g-space-0-5"></div>
          <div className='buttons'>
            {
              (loading)
                ?
                <>
                  {
                    (formData.ts_created) &&
                    <button
                      type='button'
                      className={`g-button small-btn red ${screenSize !== 'mobile' ? 'left-item' : ''}`}>
                      Delete
                    </button>
                  }
                  <button
                    type='button'
                    className={`g-button ${screenSize === 'mobile' ? 'med-btn' : ''}`}>
                    Cancel
                  </button>
                  <button
                    type='button'
                    className={`g-button ${screenSize === 'mobile' ? 'med-btn' : ''}`}>
                    <ThreeDotsLoader />
                  </button>
                </>
                :
                <>
                  {
                    (formData.ts_created) &&
                    <ElementDelete
                      element={formData}
                      style='button'
                      classText={screenSize !== 'mobile' ? 'left-item' : ''} />
                  }
                  <button
                    type='button'
                    className={`g-button ${screenSize === 'mobile' ? 'med-btn' : ''}`}
                    onClick={() => cancelCreate()}>
                    Cancel
                  </button>
                  <button
                    type='button'
                    className={`g-button submit-button`}
                    onClick={() => handleSubmit()}>
                    Save Loot Key
                  </button>
                </>
            }
          </div>
        </div>
        {
          (screenSize !== 'mobile' && screenSize !== 'tablet') &&
          <div >
            <div className='g-card fixed-card'>
              <button
                className='g-button med-btn submit-button'
                onClick={() => handleSubmit()}
              >
                Save Loot Key
              </button>
            </div>
          </div>
        }
      </div>
      <ModalAlert show={alertMessage} cancel={() => setAlertMessage(null)} message={alertMessage} />
      <ModalConfirm show={confirmMessage} cancel={() => setConfirmMessage(null)} message={confirmMessage} onApprove={approveRef.current} />
      <UpgradeRequiredModal
        show={showUpgradeModal}
        cancel={() => setShowUpgradeModal(null)}
        message={showUpgradeModal}
      />
    </div>
  )
}

export default LootKeysForm